import { Button, Col, Modal, Row } from 'antd';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { useCurrentUser } from 'store/users/selectors';
import { useNonNativeEbookHighlightes } from 'store/ebookNonNativeImageHighLighter/selectors';
import { handleNonNativeEbookListHighlights } from 'store/ebookNonNativeImageHighLighter/thunks';
import {
    appendVideoDownloadDateTimeToFileName,
    downloadVideoLikePreview,
    getVideoDownloadDateTimeStamp,
    isCloudflareUrl,
} from 'utils/methods/cloudflareVideoDownload';

const isPlayerOrSiteOnlyUrl = (urlString) => {
    try {
        const u = urlString.toLowerCase();
        if (u.includes('youtube.com') || u.includes('youtu.be') || u.includes('youtube-nocookie.com')) {
            return true;
        }
        if (u.includes('vimeo.com')) {
            return true;
        }
        return false;
    } catch {
        return true;
    }
};

const collectDefinitionVideoUrls = (ebookHighlights, bookId, pageId) => {
    if (bookId == null || pageId == null || !Array.isArray(ebookHighlights) || ebookHighlights.length === 0) {
        return [];
    }
    if (ebookHighlights[0].book_id !== bookId) {
        return [];
    }
    const pageItem = ebookHighlights.find((h) => parseInt(h.page_id, 10) === parseInt(pageId, 10));
    const rectangles = pageItem?.data?.rectangles;
    if (!Array.isArray(rectangles)) {
        return [];
    }
    const urls = [];
    const seen = new Set();
    rectangles.forEach((r) => {
        const v = typeof r?.videoFile === 'string' ? r.videoFile.trim() : '';
        if (!v || seen.has(v)) return;
        if (v.startsWith('http') || v.startsWith('blob:')) {
            seen.add(v);
            urls.push(v);
        }
    });
    return urls;
};

const openUrlFallback = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MS_PAUSE_BETWEEN_EACH_VIDEO = 900;

export default function DefinitionVideosDownloadToolbar({ bookId, pageId }) {
    const showAlert = useAlert();
    const dispatch = useDispatch();
    const ebookHighlights = useNonNativeEbookHighlightes();
    const currentUser = useCurrentUser();

    useEffect(() => {
        if (bookId && currentUser?.id) {
            dispatch(handleNonNativeEbookListHighlights(bookId));
        }
    }, [bookId, currentUser?.id, dispatch]);

    const definitionVideoUrls = useMemo(
        () => collectDefinitionVideoUrls(ebookHighlights, bookId, pageId),
        [ebookHighlights, bookId, pageId],
    );

    const handleDownloadAllDefinitionVideos = () => {
        const urls = definitionVideoUrls;
        if (urls.length === 0) {
            showAlert('No definition videos on this page.', 'info');
            return;
        }
        Modal.confirm({
            title: 'Download all Definition videos?',
            content: `${urls.length} video file(s) will be downloaded for this page.`,
            okText: 'Download',
            cancelText: 'Cancel',
            onOk: async () => {
                const quiet = urls.length > 1;
                const downloadStamp = getVideoDownloadDateTimeStamp();
                const settled = [];
                for (let i = 0; i < urls.length; i += 1) {
                    if (i > 0) {
                        await delay(MS_PAUSE_BETWEEN_EACH_VIDEO);
                    }
                    const url = urls[i];
                    let fileName = `page-${pageId}-video-${i + 1}.mp4`;
                    try {
                        const pathname = new URL(url).pathname;
                        const last = pathname.split('/').filter(Boolean).pop();
                        if (last && /\.(mp4|webm|mov|m4v|ogg)(\?.*)?$/i.test(last)) {
                            fileName = last.replace(/\?.*$/, '');
                        }
                    } catch {
                    }
                    fileName = appendVideoDownloadDateTimeToFileName(fileName, downloadStamp, i, urls.length);
                    try {
                        if (isPlayerOrSiteOnlyUrl(url) && !isCloudflareUrl(url)) {
                            openUrlFallback(url);
                            settled.push({ status: 'fulfilled', value: { type: 'player' } });
                        } else {
                            await downloadVideoLikePreview({
                                url,
                                fileName,
                                showAlert,
                                quiet,
                            });
                            settled.push({ status: 'fulfilled', value: { type: 'saved' } });
                        }
                    } catch (err) {
                        Sentry.captureException(err);
                        try {
                            openUrlFallback(url);
                        } catch (e2) {
                            Sentry.captureException(e2);
                        }
                        settled.push({ status: 'fulfilled', value: { type: 'fallback' } });
                    }
                }
                let savedFileCount = 0;
                let playerPageOpenedCount = 0;
                let fallbackAfterFailedFetch = 0;
                settled.forEach((r) => {
                    if (r.status !== 'fulfilled' || !r.value) return;
                    const { type } = r.value;
                    if (type === 'saved') savedFileCount += 1;
                    else if (type === 'player') playerPageOpenedCount += 1;
                    else if (type === 'fallback') fallbackAfterFailedFetch += 1;
                });
                if (fallbackAfterFailedFetch > 0) {
                    showAlert(
                        'Some links could not be saved. Those were opened in a new tab instead.',
                        'info',
                    );
                } else if (savedFileCount > 0) {
                    showAlert('Downloads finished.', 'success');
                } else if (playerPageOpenedCount > 0) {
                    showAlert('Opened in new tabs.', 'success');
                }
            },
        });
    };

    return (
        <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
            <Col>
                <Button type="default" size="small" onClick={handleDownloadAllDefinitionVideos}>
                    Download all definition videos
                </Button>
            </Col>
        </Row>
    );
}
